import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ExternalLink, Search, FileText, Filter, X, Building2, User, Landmark, ShieldCheck, CornerDownRight } from "lucide-react";
import { entitySlug } from "../components/EntityCard";
import SEO from "../components/SEO";
import ShareDock from "../components/ShareDock";
import { ContinueInvestigationCTA } from "../components/PremiumLock";
import { GhostSourceCard } from "../components/GhostCards";
import { trackSourceView } from "../lib/analytics";
import { useSourceReader } from "../components/SourceReader";

const FUNDING_STYLES = {
    independent: { text: "Independent", className: "bg-[#2F80ED]/10 text-[#56CCF2] border-[#2F80ED]/30" },
    industry: { text: "Industry-funded", className: "bg-[#243044] text-[#94A3B8] border-[#243044]" },
    government: { text: "Government", className: "bg-[#56CCF2]/10 text-[#56CCF2] border-[#56CCF2]/30" },
    unknown: { text: "Funding unknown", className: "bg-[#0B1220] text-[#64748B] border-[#243044]" },
};

function EntityChip({ name, type }) {
    const Icon = type === "person" ? User : type === "company" ? Building2 : Landmark;
    const slug = entitySlug(name);
    const inner = (
        <>
            <Icon className="w-3 h-3 text-[#56CCF2]" />
            {name}
        </>
    );
    const cls = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B1220] border border-[#243044] text-[11px] text-[#CBD5E1] hover:border-[#2F80ED] hover:text-white transition-colors";
    return slug ? (
        <Link to={`/entity/${slug}`} className={cls}>{inner}</Link>
    ) : (
        <span className={cls}>{inner}</span>
    );
}

function SourceCard({ source, index, highlighted, registerRef }) {
    const { openSource } = useSourceReader();
    const funding = FUNDING_STYLES[source.funding || "unknown"] || FUNDING_STYLES.unknown;
    const graph = source.graph;
    const openReader = (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        openSource(source);
    };

    return (
        <li
            ref={(el) => registerRef && registerRef(source.key, el)}
            data-testid={`vault-source-${index}`}
            id={`vault-source-${source.key}`}
            className={`bg-[#111827] border rounded-2xl p-6 transition-all scroll-mt-32 ${highlighted ? "border-[#56CCF2] shadow-[0_0_0_3px_rgba(86,204,242,0.18)]" : "border-[#243044] hover:border-[#2F80ED]/50"}`}
        >
            <div className="flex items-start gap-5">
                <div className="text-xs font-mono font-bold text-[#56CCF2] mt-0.5 min-w-[28px]">
                    {String(index + 1).padStart(3, "0")}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                        <p className="text-base font-semibold text-white leading-snug">
                            {source.title || "Untitled source"}
                        </p>
                        <span
                            data-testid="vault-source-funding"
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium border ${funding.className}`}
                        >
                            {funding.text}
                        </span>
                    </div>

                    <div className="text-xs text-[#94A3B8] flex flex-wrap gap-x-3 gap-y-1 mb-3">
                        {source.publisher && <span className="font-medium text-[#CBD5E1]">{source.publisher}</span>}
                        {source.source_type_canonical && <span className="text-[#64748B]">· {source.source_type_canonical}</span>}
                        {source.date && <span className="text-[#64748B]">· {source.date}</span>}
                    </div>

                    {source.notes && (
                        <p className="text-sm text-[#94A3B8] leading-relaxed mb-4">{source.notes}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-4 mb-4">
                        {source.url && (
                            <a
                                data-testid="vault-source-url"
                                href={source.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={openReader}
                                className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white transition-colors"
                            >
                                <ExternalLink className="w-3 h-3" />
                                View source
                            </a>
                        )}
                        {source.document_url && (
                            <a
                                href={source.document_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={openReader}
                                className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white transition-colors"
                            >
                                <FileText className="w-3 h-3" />
                                Document
                            </a>
                        )}
                        {source.verified_at && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#64748B]">
                                <ShieldCheck className="w-3 h-3" />
                                Verified {new Date(source.verified_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                            </span>
                        )}
                    </div>

                    {source.topics && source.topics.length > 0 && (
                        <div className="border-t border-[#243044] pt-3">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
                                Cited in {source.topics.length} {source.topics.length === 1 ? "investigation" : "investigations"}
                                <span className="ml-2 text-[#56CCF2]">· via shared source</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {source.topics.map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={`/topic/${t.slug}`}
                                        className="px-2.5 py-1 rounded-full bg-[#0B1220] border border-[#243044] text-[11px] text-[#CBD5E1] hover:border-[#2F80ED] hover:text-white transition-colors"
                                    >
                                        {t.title}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Graph payload — only present when this source was deep-linked */}
                    {graph && (
                        <div className="mt-5 pt-5 border-t border-[#243044] space-y-5" data-testid="vault-source-graph">
                            {graph.entities?.length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">
                                        Entities referenced alongside this source
                                        <span className="ml-2 text-[#56CCF2]">· via shared investigation</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {graph.entities.map((e, i) => <EntityChip key={i} name={e.name} type={e.type} />)}
                                    </div>
                                </div>
                            )}
                            {graph.timeline_events?.length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">
                                        Timeline events from the same investigation
                                        <span className="ml-2 text-[#56CCF2]">· documented connection</span>
                                    </div>
                                    <ul className="space-y-2">
                                        {graph.timeline_events.slice(0, 8).map((ev, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm">
                                                <CornerDownRight className="w-3.5 h-3.5 mt-1 text-[#56CCF2] shrink-0" />
                                                <div className="flex-1">
                                                    <span className="text-[#64748B] text-[10px] uppercase tracking-wider mr-2">{ev.date}</span>
                                                    <span className="text-[#CBD5E1]">{ev.title}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
}

export default function SourceVaultPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const sourceParam = searchParams.get("source") || "";

    const [items, setItems] = useState([]);
    const [gate, setGate] = useState({ is_member: true });
    const [pinnedItem, setPinnedItem] = useState(null); // the deep-linked source with full graph
    const [facets, setFacets] = useState({ total: 0, source_types: [], fundings: [], publishers: [] });
    const [loading, setLoading] = useState(true);

    const [q, setQ] = useState("");
    const [sourceType, setSourceType] = useState("");
    const [funding, setFunding] = useState("");
    const [publisher, setPublisher] = useState("");

    const cardRefs = useRef({});
    const registerRef = (key, el) => { if (el) cardRefs.current[key] = el; };

    useEffect(() => {
        api.get("/sources/facets").then((r) => setFacets(r.data)).catch(() => {});
    }, []);

    // Fetch the full deep-linked source separately (with graph payload) so it
    // remains visible even if the user changes other filters.
    useEffect(() => {
        if (!sourceParam) { setPinnedItem(null); return; }
        api.get(`/sources?source=${encodeURIComponent(sourceParam)}`)
            .then((r) => {
                const payload = r.data || {};
                const arr = Array.isArray(payload) ? payload : (payload.sources || []);
                setPinnedItem(arr[0] || null);
            })
            .catch(() => setPinnedItem(null));
    }, [sourceParam]);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (sourceType) params.set("source_type", sourceType);
        if (funding) params.set("funding", funding);
        if (publisher) params.set("publisher", publisher);
        const t = setTimeout(() => {
            api.get(`/sources${params.toString() ? "?" + params.toString() : ""}`)
                .then((r) => {
                    const payload = r.data || {};
                    if (Array.isArray(payload)) {
                        setItems(payload);
                        setGate({ is_member: true });
                    } else {
                        setItems(payload.sources || []);
                        setGate(payload._gate || { is_member: true });
                    }
                })
                .catch(() => { setItems([]); setGate({ is_member: true }); })
                .finally(() => setLoading(false));
        }, 220);
        return () => clearTimeout(t);
    }, [q, sourceType, funding, publisher]);

    // Scroll to the pinned source card once it's rendered
    useEffect(() => {
        if (!pinnedItem) return;
        trackSourceView(pinnedItem.key, { title: pinnedItem.title });
        const el = cardRefs.current[pinnedItem.key];
        if (el) {
            setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
        }
    }, [pinnedItem, items]);

    const activeFilters = useMemo(() => [sourceType, funding, publisher].filter(Boolean).length, [sourceType, funding, publisher]);

    const clear = () => { setQ(""); setSourceType(""); setFunding(""); setPublisher(""); };
    const clearPinned = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("source");
        setSearchParams(next, { replace: true });
        setPinnedItem(null);
    };

    // De-duplicate: if pinned source already exists in items, use the items copy
    // (it'll be the same record but we'll prepend the pinned graph-enriched one).
    const otherItems = pinnedItem
        ? items.filter((s) => s.key !== pinnedItem.key)
        : items;

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <SEO
                title="Source Vault"
                description="The investigative citation index across the library. Every peer-reviewed study, government filing, court record, regulator decision, and source we cite — searchable, filterable, attributed."
                canonicalPath="/sources"
            />
            <ShareDock
                title={pinnedItem ? `Source — ${pinnedItem.title}` : "Source Vault"}
                description={pinnedItem ? `Cited in ${pinnedItem.topics?.length || 0} investigations.` : "The investigative citation index across the library."}
                type="source"
                id={pinnedItem ? pinnedItem.key : "vault-index"}
            />
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-[#243044]">
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-[#0B1220]/85 to-[#0B1220]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 sm:pt-28 sm:pb-16">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#111827] border border-[#243044] mb-8">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#56CCF2] animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                                Source Vault
                            </span>
                        </div>
                        <h1 data-testid="source-vault-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6">
                            The investigative<br />citation index.
                        </h1>
                        <p className="text-lg text-[#94A3B8] leading-relaxed max-w-2xl">
                            Every source cited across the library — peer-reviewed studies, government filings, court records, regulator decisions, investigative reporting, NGO archives. Each source is a node in the research graph: filter, follow, and trace which investigations rely on it.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8 text-xs text-[#64748B]">
                            <span data-testid="vault-total"><span className="text-white font-bold">{facets.total}</span> unique sources indexed</span>
                            <span>· <span className="text-white font-bold">{facets.source_types.length}</span> source types</span>
                            <span>· <span className="text-white font-bold">{facets.publishers.length}</span> publishers</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Deep-linked source banner */}
            {pinnedItem && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <div data-testid="vault-pinned-banner" className="flex items-start justify-between gap-4 bg-[#111827] border border-[#56CCF2]/40 rounded-2xl p-5">
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] mb-1">Source record</div>
                            <p className="text-sm font-semibold text-white leading-snug truncate">{pinnedItem.title}</p>
                            <p className="text-xs text-[#64748B] mt-1">
                                Cited in {pinnedItem.topics?.length || 0} investigations · {pinnedItem.graph?.entities?.length || 0} connected entities
                            </p>
                        </div>
                        <button
                            data-testid="vault-pinned-clear"
                            onClick={clearPinned}
                            className="text-xs text-[#94A3B8] hover:text-white inline-flex items-center gap-1.5"
                        >
                            <X className="w-3.5 h-3.5" />
                            Clear
                        </button>
                    </div>
                </section>
            )}

            {/* Filters */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <div className="bg-[#111827] border border-[#243044] rounded-2xl p-5 space-y-4">
                    <div className="flex items-center gap-3 bg-[#0B1220] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Search className="w-4 h-4 text-[#64748B] shrink-0" />
                        <input
                            data-testid="vault-search-input"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search sources by title, publisher, URL, or notes…"
                            className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] flex-1"
                        />
                        {q && (
                            <button onClick={() => setQ("")} className="text-[#64748B] hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Source type</label>
                            <select
                                data-testid="vault-filter-type"
                                value={sourceType}
                                onChange={(e) => setSourceType(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                <option value="">All types</option>
                                {facets.source_types.map((t) => (
                                    <option key={t.value} value={t.value}>{t.value} ({t.count})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Funding</label>
                            <select
                                data-testid="vault-filter-funding"
                                value={funding}
                                onChange={(e) => setFunding(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                <option value="">All funding</option>
                                {facets.fundings.map((f) => (
                                    <option key={f.value} value={f.value}>{f.value} ({f.count})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Publisher</label>
                            <select
                                data-testid="vault-filter-publisher"
                                value={publisher}
                                onChange={(e) => setPublisher(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                <option value="">All publishers</option>
                                {facets.publishers.map((p) => (
                                    <option key={p.value} value={p.value}>{p.value} ({p.count})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {(activeFilters > 0 || q) && (
                        <div className="flex items-center justify-between pt-1">
                            <div className="text-xs text-[#64748B] inline-flex items-center gap-2">
                                <Filter className="w-3 h-3" />
                                {activeFilters > 0 && `${activeFilters} filter${activeFilters === 1 ? "" : "s"} active`}
                                {q && (activeFilters > 0 ? " · search active" : "search active")}
                            </div>
                            <button
                                data-testid="vault-clear-filters"
                                onClick={clear}
                                className="text-xs text-[#56CCF2] hover:text-white"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Results */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex items-baseline justify-between mb-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium">
                        Indexed sources
                    </div>
                    <div data-testid="vault-result-count" className="text-xs text-[#64748B]">
                        {loading ? "Loading…" : `${otherItems.length + (pinnedItem ? 1 : 0)} result${otherItems.length + (pinnedItem ? 1 : 0) === 1 ? "" : "s"}`}
                    </div>
                </div>

                {loading && !pinnedItem ? (
                    <div className="text-center text-sm text-[#64748B] py-20">Loading source index…</div>
                ) : (otherItems.length === 0 && !pinnedItem) ? (
                    <div className="text-center py-20">
                        <p className="text-base text-[#CBD5E1] mb-2">No sources match your filters.</p>
                        <button onClick={clear} className="text-sm text-[#56CCF2] hover:text-white">Clear filters</button>
                    </div>
                ) : (
                    <ul className="space-y-4">
                        {pinnedItem && (
                            <SourceCard
                                source={pinnedItem}
                                index={0}
                                highlighted
                                registerRef={registerRef}
                            />
                        )}
                        {otherItems.map((s, i) => (
                            <SourceCard
                                key={s.key}
                                source={s}
                                index={pinnedItem ? i + 1 : i}
                                registerRef={registerRef}
                            />
                        ))}
                    </ul>
                )}

                {/* Premium continuation: ghost sources + CTA */}
                {!gate.is_member && (gate.ghosts?.sources?.length || 0) > 0 && (
                    <div data-testid="vault-premium" className="mt-10 space-y-3">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-4">
                            {gate.ghosts.sources.length} further source{gate.ghosts.sources.length === 1 ? "" : "s"} in the members archive
                        </div>
                        {gate.ghosts.sources.slice(0, 10).map((g, i) => (
                            <GhostSourceCard key={i} item={g} />
                        ))}
                        <div className="mt-8">
                            <ContinueInvestigationCTA
                                label="Open the full Source Vault"
                                subline="Every cited source with URL, document, verification date, and the full graph of investigations and entities each one connects — reserved for members."
                                testId="vault-premium-cta"
                            />
                        </div>
                    </div>
                )}

                {/* Pinned-but-gated hint */}
                {pinnedItem && !gate.is_member && !pinnedItem.graph && (
                    <div className="mt-10">
                        <ContinueInvestigationCTA
                            compact
                            label="Open this source as a research node"
                            subline="Members see the full graph — every entity referenced alongside this source, every documented timeline event from the investigations that cite it, and the source URL itself."
                            testId="vault-pinned-premium-cta"
                        />
                    </div>
                )}
            </section>

            <Footer />
        </div>
    );
}
